import * as Types from '../types';
import { Environment } from '../../../config';
import request from '../../../utils/request';

interface IIdentificationDetails {
  registration_id: number;
  username: string;
  user_id: number;
  company_name: string;
  intermidiary: number;
  vat_num: string;
  vat_group: string;
  non_national_tax_ref_num: string;
  non_place_of_business: number;
  non_issuing_country: number;
  import_national_tax_ref_num: string;
  import_place_of_business: number;
  import_issuing_country: number;
  trading_name: Array<string>;
  is_agent: boolean;
  status: number;
  state: string;
}

interface IContactDetails {
  name: string;
  first_name: string;
  last_name: string;
  street: string;
  building_identifier: string;
  post_code: string;
  city: string;
  country_id: number;
  telephone: string;
  email: string;
  website_url: Array<string>;
}

interface IPaymentDetails {
  iban: string;
  account_holder: string;
  bic: string;
}

interface IFirstSupplyDetails {
  first_supply_date: Date;
  registration_date: Date;
}

interface IFixedEstablishments {
  issuing: number;
  vat_id_num: string;
  name: string;
  fixed_establish_address_id: number;
  fixed_establish_id: number;
  country_id: number;
  address_free: string;
  street: string;
  building_identifier: string;
  suite_identifier: string;
  floor_identifier: string;
  district_name: string;
  pob: string;
  post_code: string;
  city: string;
  country_sub_entity: string;
  other_local_id: number;
  start_date: Date;
}

interface IPreviousUseOfSpecialSchemes {
  country_id: number;
  vat_id_num: string;
  id_type_id: string;
}

interface IChangeIdentification {
  vat_id_num: string;
  country_id: number;
  applicant_type_id: string;
}

interface IAdministration {
  registration_comments: string;
  registration_date: Date;
  commencement_date: Date;
  change_date: Date;
}

interface IIdentificationInOtherEuCountries {
  country_id: number;
  vat_id_num: string;
  id_type_id: number;
}

interface IResponseDetailOutput {
  identification_details: IIdentificationDetails;
  contact_details: IContactDetails;
  payment_details: Array<IPaymentDetails>;
  first_supply_details: Array<IFirstSupplyDetails>;
  fixed_establish_details: Array<IFixedEstablishments>;
  previous_use_of_special_schemes: Array<IPreviousUseOfSpecialSchemes>;
  change_identification: Array<IChangeIdentification>;
  administration: Array<IAdministration>;
  identification_in_other_countries: Array<IIdentificationInOtherEuCountries>;
}

export const fethRegistrationDetail = async (
  id: number
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/registration/section/${id}`
  );
  let _data: IResponseDetailOutput = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: _data,
    status: data.status,
  };
  return output;
};

interface IResponseUpdateRegistration {
  id: number;
}

export const saveTemporaryRegistration = async (
  payload: any,
  id: number,
  urlExtension: string = ''
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/save/temporary/${urlExtension}`,
    payload
  );
  let _data: IResponseUpdateRegistration = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const finalSubmitRegistration = async (
  payload: any,
  id: number,
  urlExtension: string = ''
): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/save/final/${urlExtension}`,
    payload
  );
  let _data: IResponseUpdateRegistration = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};

export const approveRegistration = async (
  comments: string,
  id: number,
  registration_date: Date,
  commencement_date: Date,
  urlExtension: 'exclude' | '' = ''
) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/approve/${urlExtension}`,
    urlExtension === 'exclude'
      ? {
          exclusion_decision_date: registration_date,
          exclusion_effective_date: commencement_date,
          exclusion_comments: comments,
        }
      : {
          comments,
          registration_date,
          commencement_date,
        }
  );
  return data;
};

export const rejectRegistration = async (
  comments: string,
  id: number,
  registration_date: Date,
  commencement_date: Date,
  urlExtension: 'exclude' | '' = ''
) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/reject/${urlExtension}`,
    urlExtension === 'exclude'
      ? {
          exclusion_decision_date: registration_date,
          exclusion_effective_date: commencement_date,
          exclusion_comments: comments,
        }
      : {
          comments,
          registration_date,
          commencement_date,
        }
  );
  return data;
};

export const updateRegistration = async (id: number) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/update`
  );
  return data;
};

export const excludeRegistration = async (id: number) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/exclude`
  );
  return data;
};

export const cancelRegistration = async (id: number) => {
  const { data }: any = await request(
    'patch',
    `${Environment.API_BASE_URL}/api/registration/${id}/exclude/cancel`
  );
  return data;
};

interface IIdType {
  id_type_id: number;
  id_type: string;
}

export const getIdTypes = async (): Promise<Types.ResponseOutput> => {
  const { data }: any = await request(
    'get',
    `${Environment.API_BASE_URL}/api/idtype`
  );
  let _data: IIdType = data.data;
  let output: Types.ResponseOutput = {
    message: data.message,
    data: data.data,
    status: data.status,
  };
  return output;
};
